import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

// ─── Design tokens ────────────────────────────────────────────────────────────
const M = '#06060c'
const G = '#d4a843'
const IN = '#0ea5e9'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'
const SUCCESS = '#4ade80'
const ERROR = '#f87171'

// ─── Types ────────────────────────────────────────────────────────────────────
interface Employee {
    id: string
    email: string
    name: string
    created_at: string
    hide_grades: boolean
    progress: {
        total_degrees: number
        completed_lessons: number
        average_mastery: number
        last_active_at: string | null
    }
    prep_pathway_mode: 'guided' | 'bridge_only' | 'direct'
}

interface FacilityInfo {
    id: string
    name: string
    max_seats: number
    used_seats: number
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CorporateAdminPage() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { user, isLoading: authLoading } = useAuth()

    const [facility, setFacility] = useState<FacilityInfo | null>(null)
    const [employees, setEmployees] = useState<Employee[]>([])
    const [loading, setLoading] = useState(true)
    const [isAdmin, setIsAdmin] = useState(false)
    const [isMobile, setIsMobile] = useState(false)

    // Invite modal
    const [showInvite, setShowInvite] = useState(false)
    const [inviteEmail, setInviteEmail] = useState('')
    const [inviteName, setInviteName] = useState('')
    const [inviteLoading, setInviteLoading] = useState(false)
    const [inviteResult, setInviteResult] = useState<{ ok: boolean; msg: string } | null>(null)

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 820)
        check()
        window.addEventListener('resize', check)
        return () => window.removeEventListener('resize', check)
    }, [])

    useEffect(() => {
        if (!authLoading && !user) navigate('/signin', { replace: true })
    }, [user, authLoading, navigate])

    // Fetch facility and employees
    const fetchData = useCallback(async () => {
        if (!user) return
        setLoading(true)

        try {
            // Check if user is facility admin
            const { data: profile } = await supabase
                .from('users')
                .select('facility_id, facility_role')
                .eq('id', user.id)
                .single()

            if (!profile?.facility_id || profile.facility_role !== 'admin') {
                setIsAdmin(false)
                setLoading(false)
                return
            }

            setIsAdmin(true)
            const facilityId = profile.facility_id

            // Get facility info
            const { data: fac } = await supabase
                .from('facilities')
                .select('id, name, max_seats, used_seats')
                .eq('id', facilityId)
                .single()

            if (fac) {
                setFacility(fac as FacilityInfo)
            }

            // Get employees (members in this facility)
            const { data: members } = await supabase
                .from('users')
                .select('id, email, name, created_at, hide_grades, prep_pathway_mode')
                .eq('facility_id', facilityId)
                .neq('facility_role', 'admin')
                .order('created_at', { ascending: false })

            const employeeList: Employee[] = []

            for (const member of (members || [])) {
                const { data: enrolments } = await supabase
                    .from('class_enrollments')
                    .select('mastery_score, last_active_at, progress_state, classes (facility_id)')
                    .eq('student_id', member.id)

                const uniEnrolments = (enrolments || []).filter(e => {
                    const cls = Array.isArray(e.classes) ? e.classes[0] : e.classes
                    return (cls as any)?.facility_id === '00000000-0000-0000-0000-000000000002'
                })

                const totalDegrees = uniEnrolments.length
                const completedLessons = uniEnrolments.reduce((s, e) => {
                    const ps = e.progress_state as any
                    return s + (ps?.total_lessons_completed || 0)
                }, 0)
                const avgMastery = uniEnrolments.length > 0
                    ? Math.round(uniEnrolments.reduce((s, e) => s + (Number(e.mastery_score) || 0), 0) / uniEnrolments.length)
                    : 0

                const lastActive = uniEnrolments.reduce((latest, e) => {
                    if (!e.last_active_at) return latest
                    if (!latest) return e.last_active_at
                    return new Date(e.last_active_at) > new Date(latest) ? e.last_active_at : latest
                }, null as string | null)

                employeeList.push({
                    id: member.id,
                    email: member.email || '',
                    name: member.name || member.email?.split('@')[0] || 'Employee',
                    created_at: member.created_at,
                    hide_grades: (member as any).hide_grades ?? false,
                    progress: {
                        total_degrees: totalDegrees,
                        completed_lessons: completedLessons,
                        average_mastery: avgMastery,
                        last_active_at: lastActive
                    },
                    prep_pathway_mode: member.prep_pathway_mode || 'guided'
                })
            }

            setEmployees(employeeList)
        } catch (err) {
            console.error('CorporateAdmin load error:', err)
        } finally {
            setLoading(false)
        }
    }, [user])

    useEffect(() => { fetchData() }, [fetchData])

    // Send invite
    async function handleInvite() {
        if (!inviteEmail.trim()) return
        setInviteLoading(true)
        setInviteResult(null)

        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (!session?.access_token) throw new Error('Not authenticated')

            const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl

            const res = await fetch(`${supabaseUrl}/functions/v1/invite-university-employee`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    email: inviteEmail.trim(),
                    name: inviteName.trim() || undefined,
                }),
            })

            const data = await res.json()

            if (!res.ok) {
                setInviteResult({ ok: false, msg: data.error || 'Failed to send invite' })
            } else {
                setInviteResult({ ok: true, msg: data.message || `Invitation sent to ${inviteEmail}` })
                setInviteEmail('')
                setInviteName('')
                // Refresh employee list
                setTimeout(() => fetchData(), 1500)
            }
        } catch (err) {
            setInviteResult({ ok: false, msg: (err as Error).message || 'Failed to send invite' })
        } finally {
            setInviteLoading(false)
        }
    }

    // Toggle hide grades
    async function toggleHideGrades(employeeId: string, current: boolean) {
        try {
            await supabase
                .from('users')
                .update({ hide_grades: !current })
                .eq('id', employeeId)

            setEmployees(prev => prev.map(e =>
                e.id === employeeId ? { ...e, hide_grades: !current } : e
            ))
        } catch (err) {
            console.error('toggleHideGrades error:', err)
        }
    }

    // Update pathway mode
    async function updatePathwayMode(employeeId: string, mode: 'guided' | 'bridge_only' | 'direct') {
        try {
            await supabase
                .from('users')
                .update({
                    prep_pathway_mode: mode,
                    prep_pathway_set_at: new Date().toISOString(),
                    prep_pathway_set_by: user?.id
                })
                .eq('id', employeeId)

            setEmployees(prev => prev.map(e =>
                e.id === employeeId ? { ...e, prep_pathway_mode: mode } : e
            ))
        } catch (err) {
            console.error('updatePathwayMode error:', err)
        }
    }

    // ─── Loading / Auth check ───────────────────────────────────────────────────
    if (authLoading || loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
                    style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(212,168,67,0.15)', borderTopColor: G }}
                />
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div style={{ background: M, minHeight: '100vh', color: TX, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', system-ui, sans-serif" }}>
                <div style={{ textAlign: 'center', maxWidth: 400, padding: 40 }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
                    <h2 style={{ fontWeight: 800, fontSize: 24, marginBottom: 8 }}>
                        {t('university.corporate.admin.noAccess', 'Admin Access Required')}
                    </h2>
                    <p style={{ color: MU, fontSize: 14, marginBottom: 24 }}>
                        {t('university.corporate.admin.noAccessHint', 'You need to be a corporate facility admin to access this dashboard.')}
                    </p>
                    <button
                        onClick={() => navigate('/dashboard')}
                        style={{
                            background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c',
                            border: 'none', borderRadius: 12, padding: '12px 24px',
                            fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        }}
                    >
                        {t('university.corporate.admin.goBack', 'Go to Dashboard')}
                    </button>
                </div>
            </div>
        )
    }

    const seatsRemaining = (facility?.max_seats || 0) - (facility?.used_seats || 0)

    return (
        <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '40px 24px 80px' }}>

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>
                    <button
                        onClick={() => navigate('/dashboard')}
                        style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0, fontWeight: 600 }}
                    >
                        {t('university.corporate.admin.back', '← BACK TO DASHBOARD')}
                    </button>
                    <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2.4px', marginBottom: 8, textTransform: 'uppercase' }}>
                        {facility?.name || t('university.corporate.admin.eyebrow', 'CORPORATE DASHBOARD')}
                    </p>
                    <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 900, margin: '0 0 8px', letterSpacing: '-0.8px' }}>
                        {t('university.corporate.admin.title', 'Employee Learning Dashboard')}
                    </h1>
                    <p style={{ color: MU, fontSize: 14, marginBottom: 32, fontWeight: 500 }}>
                        {t('university.corporate.admin.subtitle', 'Manage seats, invite employees, and track their progress.')}
                    </p>
                </motion.div>

                {/* Stats row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                    gap: isMobile ? 12 : 20,
                    marginBottom: isMobile ? 32 : 48,
                }}>
                    {[
                        { label: t('university.corporate.admin.statsSeats', 'Total Seats'), value: facility?.max_seats || 0, icon: '💺', color: IN },
                        { label: t('university.corporate.admin.statsUsed', 'Seats Used'), value: facility?.used_seats || 0, icon: '👤', color: G },
                        { label: t('university.corporate.admin.statsRemaining', 'Available'), value: seatsRemaining, icon: '✅', color: SUCCESS },
                        { label: t('university.corporate.admin.statsEmployees', 'Employees'), value: employees.length, icon: '👥', color: '#0ea5e9' },
                    ].map((s, i) => (
                        <motion.div
                            key={s.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.07 }}
                            style={{
                                background: CA, border: `1px solid ${BR}`,
                                borderRadius: 20, padding: isMobile ? '20px' : '24px',
                            }}
                        >
                            <div style={{ fontSize: isMobile ? 22 : 26, marginBottom: 16 }}>{s.icon}</div>
                            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 900, color: s.color, letterSpacing: '-1px', lineHeight: 1 }}>{s.value}</div>
                            <div style={{ fontSize: 11, color: MU, marginTop: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                        </motion.div>
                    ))}
                </div>

                {/* Invite button */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 32, flexWrap: 'wrap' }}>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowInvite(true)}
                        disabled={seatsRemaining <= 0}
                        style={{
                            background: seatsRemaining > 0 ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)',
                            color: seatsRemaining > 0 ? '#06060c' : MU,
                            border: 'none', borderRadius: 14, padding: '14px 28px',
                            fontSize: 14, fontWeight: 800, cursor: seatsRemaining > 0 ? 'pointer' : 'not-allowed',
                        }}
                    >
                        + {t('university.corporate.admin.inviteBtn', 'Invite Employee')}
                    </motion.button>
                    {seatsRemaining <= 0 && (
                        <p style={{ color: ERROR, fontSize: 13, fontWeight: 600, alignSelf: 'center' }}>
                            {t('university.corporate.admin.noSeats', 'No seats available. Upgrade your plan to add more.')}
                        </p>
                    )}
                </div>

                {/* Invite modal */}
                <AnimatePresence>
                    {showInvite && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
                            onClick={() => setShowInvite(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.92, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.94, opacity: 0 }}
                                onClick={e => e.stopPropagation()}
                                style={{
                                    background: '#13132a', border: `1px solid ${BR}`, borderRadius: 24,
                                    padding: 32, width: '100%', maxWidth: 440,
                                    boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
                                }}
                            >
                                <h3 style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>
                                    {t('university.corporate.admin.inviteTitle', 'Invite Employee')}
                                </h3>
                                <p style={{ color: MU, fontSize: 13, marginBottom: 24, fontWeight: 500 }}>
                                    {t('university.corporate.admin.inviteHint', 'They will receive a magic link to sign in and start learning.')}
                                </p>

                                <label style={{ fontSize: 12, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>
                                    {t('university.corporate.admin.inviteEmailLabel', 'Email Address')}
                                </label>
                                <input
                                    value={inviteEmail}
                                    onChange={e => setInviteEmail(e.target.value)}
                                    placeholder="employee@company.com"
                                    type="email"
                                    style={{
                                        width: '100%', padding: '12px 16px', borderRadius: 12, border: `1.5px solid ${BR}`,
                                        background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 15, fontFamily: "'Inter', sans-serif",
                                        marginBottom: 16, outline: 'none', boxSizing: 'border-box',
                                    }}
                                />

                                <label style={{ fontSize: 12, fontWeight: 700, color: MU, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' }}>
                                    {t('university.corporate.admin.inviteNameLabel', 'Name (Optional)')}
                                </label>
                                <input
                                    value={inviteName}
                                    onChange={e => setInviteName(e.target.value)}
                                    placeholder="John Doe"
                                    style={{
                                        width: '100%', padding: '12px 16px', borderRadius: 12, border: `1.5px solid ${BR}`,
                                        background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 15, fontFamily: "'Inter', sans-serif",
                                        marginBottom: 20, outline: 'none', boxSizing: 'border-box',
                                    }}
                                />

                                <AnimatePresence>
                                    {inviteResult && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0 }}
                                            style={{
                                                background: inviteResult.ok ? 'rgba(74,222,128,0.1)' : 'rgba(248,113,113,0.1)',
                                                border: `1px solid ${inviteResult.ok ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)'}`,
                                                borderRadius: 12, padding: '10px 14px', marginBottom: 16,
                                                fontSize: 13, fontWeight: 600,
                                                color: inviteResult.ok ? SUCCESS : ERROR,
                                            }}
                                        >
                                            {inviteResult.msg}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <div style={{ display: 'flex', gap: 10 }}>
                                    <button
                                        onClick={() => { setShowInvite(false); setInviteResult(null) }}
                                        style={{
                                            flex: 1, padding: '12px', borderRadius: 12, border: `1px solid ${BR}`,
                                            background: 'transparent', color: MU, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                                        }}
                                    >
                                        {t('university.corporate.admin.inviteCancel', 'Cancel')}
                                    </button>
                                    <button
                                        onClick={handleInvite}
                                        disabled={inviteLoading || !inviteEmail.trim()}
                                        style={{
                                            flex: 1, padding: '12px', borderRadius: 12, border: 'none',
                                            background: inviteEmail.trim() ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.06)',
                                            color: inviteEmail.trim() ? '#06060c' : MU,
                                            fontSize: 14, fontWeight: 700,
                                            cursor: inviteEmail.trim() ? 'pointer' : 'not-allowed',
                                            opacity: inviteLoading ? 0.7 : 1,
                                        }}
                                    >
                                        {inviteLoading
                                            ? t('university.corporate.admin.inviteSending', 'Sending…')
                                            : t('university.corporate.admin.inviteSend', 'Send Invite')}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Employee table */}
                <h2 style={{ fontWeight: 800, fontSize: isMobile ? 20 : 24, marginBottom: 20, letterSpacing: '-0.5px' }}>
                    {t('university.corporate.admin.employeesTitle', 'Employees')}
                </h2>

                {employees.length === 0 ? (
                    <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 20, padding: '60px', textAlign: 'center' }}>
                        <div style={{ fontSize: 44, marginBottom: 16 }}>👥</div>
                        <p style={{ color: MU, fontSize: 15 }}>
                            {t('university.corporate.admin.noEmployees', 'No employees yet. Invite your first team member to get started.')}
                        </p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {employees.map((emp, i) => (
                            <motion.div
                                key={emp.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.04 }}
                                style={{
                                    background: CA, border: `1px solid ${BR}`,
                                    borderRadius: 16, padding: isMobile ? '16px' : '20px 24px',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 16, flexDirection: isMobile ? 'column' : 'row' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                            <div style={{
                                                width: 36, height: 36, borderRadius: '50%',
                                                background: `linear-gradient(135deg, ${IN}, ${G})`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 14, fontWeight: 800, color: '#06060c',
                                            }}>
                                                {emp.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p style={{ fontWeight: 700, fontSize: 15, margin: 0 }}>{emp.name}</p>
                                                <p style={{ color: MU, fontSize: 12, margin: 0 }}>{emp.email}</p>
                                            </div>
                                        </div>

                                        {/* Progress stats */}
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginLeft: 48 }}>
                                            <span style={{ fontSize: 12, color: MU }}>
                                                🎓 {emp.progress.total_degrees} {t('university.corporate.admin.degrees', 'degrees')}
                                            </span>
                                            <span style={{ fontSize: 12, color: MU }}>
                                                📖 {emp.progress.completed_lessons} {t('university.corporate.admin.lessons', 'lessons')}
                                            </span>
                                            {!emp.hide_grades && (
                                                <span style={{
                                                    fontSize: 12, fontWeight: 700,
                                                    color: emp.progress.average_mastery >= 80 ? SUCCESS : emp.progress.average_mastery >= 60 ? G : ERROR,
                                                }}>
                                                    🎯 {emp.progress.average_mastery}% {t('university.corporate.admin.mastery', 'mastery')}
                                                </span>
                                            )}
                                            {emp.hide_grades && (
                                                <span style={{ fontSize: 12, color: MU, fontStyle: 'italic' }}>
                                                    🔒 {t('university.corporate.admin.gradesHidden', 'Grades hidden')}
                                                </span>
                                            )}
                                            {emp.progress.last_active_at && (
                                                <span style={{ fontSize: 12, color: MU }}>
                                                    🕒 {new Date(emp.progress.last_active_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div style={{ display: 'flex', gap: 12, flexShrink: 0, alignItems: isMobile ? 'flex-start' : 'center', width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                            <span style={{ fontSize: 10, fontWeight: 800, color: MU, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Learning Pathway
                                            </span>
                                            <select
                                                value={emp.prep_pathway_mode}
                                                onChange={(e) => updatePathwayMode(emp.id, e.target.value as any)}
                                                style={{
                                                    background: 'rgba(255,255,255,0.04)',
                                                    border: `1px solid ${BR}`,
                                                    borderRadius: 10,
                                                    padding: '8px 12px',
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: TX,
                                                    outline: 'none',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="guided">Guided (Standard)</option>
                                                <option value="bridge_only">Skip Foundations</option>
                                                <option value="direct">Direct Entry (Skip Prep)</option>
                                            </select>
                                        </div>

                                        <button
                                            onClick={() => toggleHideGrades(emp.id, emp.hide_grades)}
                                            style={{
                                                background: 'rgba(255,255,255,0.04)', border: `1px solid ${BR}`,
                                                borderRadius: 10, padding: '8px 14px',
                                                fontSize: 11, fontWeight: 600, cursor: 'pointer', color: MU,
                                                marginTop: isMobile ? 0 : 20
                                            }}
                                        >
                                            {emp.hide_grades
                                                ? t('university.corporate.admin.showGrades', 'Show Grades')
                                                : t('university.corporate.admin.hideGrades', 'Hide Grades')}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
